import React, { useCallback } from "react";

import { Stack, TablePagination } from "@mui/material";
import MuiTable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { TableCellProps } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { QuickViewDeleteIcon, QuickViewEditIcon } from "assets/material-custom-icons";
import { Menu } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useModals, useNavigationHooks } from "hooks";
import { useUserManageActions } from "store";
import { IPaginationResponse, IRole } from "store/types";
// import { useNavigationHooks } from "hooks";
import { IPermissionItem, IUserManage } from "store/types/user-management";

import { Drawer } from "../Drawer/Drawer";

import "./Table.styles.scss";

const cellTextStyles: TableCellProps["sx"] = {
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  padding: "0 20px",
  color: "#0a3142",
  fontSize: "16px",
  height: "50px"
};

interface HeadCell {
  key: string;
  title: string;
}

const headCells: HeadCell[] = [
  {
    key: "name",
    title: "Name"
  },
  {
    key: "email",
    title: "Email"
  },
  {
    key: "phone",
    title: "Phone"
  },
  {
    key: "role",
    title: "Platform Role"
  },
  // {
  //   key: "team",
  //   title: "Team"
  // },
  // {
  //   key: "investment_entity",
  //   title: "Investment entity"
  // },
  // {
  //   key: "group",
  //   title: "Group"
  // },
  {
    key: "note",
    title: "Note"
  },
  {
    key: "",
    title: ""
  }
];

interface Props {
  refetchTable: () => void;
  handleChangePage: (newPage: number) => void;
  data: IPaginationResponse<IUserManage>;
  page: number;
  rowsPerPage: number;
  permissionList: IPermissionItem[];
}

export const Table: React.FC<Props> = ({ handleChangePage, refetchTable, data, rowsPerPage, page, permissionList }) => {
  const { total, data: rowsData } = data;
  const { setParams } = useNavigationHooks();
  const { deleteModal } = useModals();
  const { deleteUserManageAction, deleteContactAction } = useUserManageActions();

  const onDelete = useCallback((id: number, role: IRole) => {
    deleteModal({
      action: async () => {
        if (role.id === -1) {
          await deleteContactAction(id);
        } else {
          await deleteUserManageAction(id);
        }
        refetchTable();
      },
      element: "Delete",
      onPressSuccess: "Confirm",
      message:
        "Are you sure you want to delete this user? This action is irreversible and will permanently remove all associated data and permissions for this user."
    });
  }, []);

  return (
    <Stack sx={{ flexGrow: 1, overflow: "hidden" }}>
      <TableContainer
        sx={{
          borderRadius: "10px",
          border: "1px solid #dae0e2",
          borderTop: "none",
          paddingBottom: "7px",
          flexGrow: 1,
          backgroundColor: "white"
        }}
      >
        <MuiTable
          sx={{
            minWidth: 900,
            borderRadius: "10px 20px 30px 40px",
            color: "#0a3142"
          }}
          aria-labelledby="tableTitle"
        >
          <TableHead sx={{ position: "sticky", top: 0, backgroundColor: "white", height: "40px" }}>
            <TableRow sx={{ backgroundColor: "#edf1f9" }}>
              {headCells.map(({ title, key }) => (
                <TableCell
                  sx={{
                    fontSize: "15px",
                    fontWeight: "500",
                    ...cellTextStyles,
                    padding: !title.length ? "0px 5px" : "0px 20px"
                  }}
                  key={key}
                >
                  {title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsData.map((row: IUserManage) => {
              const {
                id,
                full_name,
                role,
                phone_number,
                email,
                // teams, groups, investment_entities,
                note
              } = row;
              return (
                <TableRow
                  sx={{
                    ":hover": { backgroundColor: "#f5f7f7", ".MuiTableCell-root": { opacity: 1 } }
                  }}
                  key={id}
                >
                  <TableCell
                    onClick={() =>
                      id && setParams({ search: { userManagement: id, ...(role.id === -1 && { isContact: true }) } })
                    }
                    sx={{
                      fontSize: "16px",
                      // cursor: "pointer",
                      width: "180px",
                      maxWidth: "180px",
                      ...cellTextStyles,
                      cursor: "pointer",
                      color: "#26A6AE"
                    }}
                    align="left"
                  >
                    <div className="cell-tooltip">
                      <MaterialTooltip placement="top" title={full_name}>
                        <span className="cell-span">{full_name}</span>
                      </MaterialTooltip>
                    </div>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      width: "200px",
                      maxWidth: "200px",
                      ...cellTextStyles
                    }}
                  >
                    <div className="cell-tooltip">
                      <MaterialTooltip placement="top" title={email}>
                        <span className="cell-span">{email}</span>
                      </MaterialTooltip>
                    </div>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      // width: getColumnWidth(columnsWdith[3]),
                      // maxWidth: getColumnWidth(columnsWdith[3]),
                      width: "136px",
                      maxWidth: "136px",
                      ...cellTextStyles
                    }}
                    // align="center"
                  >
                    <div className="cell-tooltip">
                      <MaterialTooltip placement="top" title={phone_number}>
                        <span className="cell-span">{phone_number}</span>
                      </MaterialTooltip>
                    </div>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      width: "90px",
                      maxWidth: "90px",
                      ...cellTextStyles
                    }}
                    // align="center"
                  >
                    {role.presentation_name}
                  </TableCell>
                  {/* <TableCell
                    align="left"
                    sx={{
                      width: "160px",
                      maxWidth: "160px",
                      color: "#0a3142",
                      ...cellTextStyles
                    }}
                    // align="center"
                  >
                    <Stack flexDirection="row" gap="4px">
                      <Typography>{teams[0]}</Typography>
                      {teams.length > 1 && (
                        <Chip
                          label={
                            <Typography
                              sx={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                fontSize: "12px"
                              }}
                            >
                              +{teams.length - 1}
                            </Typography>
                          }
                          sx={{
                            color: "#071F35",
                            height: "24px",
                            cursor: "default",
                            backgroundColor: "#F6F8FC !important"
                          }}
                        />
                      )}
                    </Stack>
                  </TableCell> */}
                  {/* <TableCell
                    align="left"
                    sx={{
                      // width: getColumnWidth(columnsWdith[6]),
                      width: "165px",
                      maxWidth: "165px",
                      ...cellTextStyles
                    }}
                  >
                    <Stack flexDirection="row" gap="4px">
                      <Typography>{investment_entities[0]}</Typography>
                      {investment_entities.length > 1 && (
                        <Chip
                          label={
                            <Typography
                              sx={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                fontSize: "12px"
                              }}
                            >
                              +{investment_entities.length - 1}
                            </Typography>
                          }
                          sx={{
                            color: "#071F35",
                            height: "24px",
                            cursor: "default",
                            backgroundColor: "#F6F8FC !important"
                          }}
                        />
                      )}
                    </Stack>
                  </TableCell> */}
                  {/* <TableCell
                    align="left"
                    sx={{
                      // width: getColumnWidth(columnsWdith[6]),
                      width: "155px",
                      maxWidth: "155px",
                      ...cellTextStyles
                    }}
                  >
                    <Stack flexDirection="row" gap="4px">
                      <Typography>{groups[0]}</Typography>
                      {groups.length > 1 && (
                        <Chip
                          label={
                            <Typography
                              sx={{
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                fontSize: "12px"
                              }}
                            >
                              +{groups.length - 1}
                            </Typography>
                          }
                          sx={{
                            color: "#071F35",
                            height: "24px",
                            cursor: "default",
                            backgroundColor: "#F6F8FC !important"
                          }}
                        />
                      )}
                    </Stack>
                  </TableCell> */}
                  <TableCell
                    align="left"
                    sx={{
                      // width: getColumnWidth(columnsWdith[6]),
                      width: "155px",
                      maxWidth: "155px",
                      ...cellTextStyles
                    }}
                  >
                    <div className="cell-tooltip">
                      <MaterialTooltip placement="top" title={note}>
                        <span className="cell-span">{note}</span>
                      </MaterialTooltip>
                    </div>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      // width: getColumnWidth(columnsWdith[6]),
                      width: "75px",
                      maxWidth: "75px",
                      opacity: 0,
                      ...cellTextStyles,
                      padding: "0px 5px"
                    }}
                  >
                    <Menu
                      options={[
                        {
                          title: "Edit",
                          icon: <QuickViewEditIcon />,
                          onClick: () =>
                            id &&
                            setParams({ search: { userManagement: id, ...(role.id === -1 && { isContact: true }) } })
                          // hidden:
                        },
                        {
                          title: "Delete",
                          icon: <QuickViewDeleteIcon />,
                          onClick: () => id && onDelete(id, role),
                          hidden: role.name === "admin"
                        }
                      ]}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </MuiTable>
        <Drawer refetch={refetchTable} permissionList={permissionList} />
      </TableContainer>
      <TablePagination
        align="center"
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, height: "40px", ".MuiTablePagination-toolbar": { minHeight: "40px" } }}
        rowsPerPageOptions={[50]}
        component="div"
        rowsPerPage={rowsPerPage}
        count={total}
        page={page - 1}
        onPageChange={(_, newPage) => {
          handleChangePage(newPage);
        }}
      />
    </Stack>
  );
};
